import React, { useState, useEffect } from "react";
import { useSpring, animated } from "react-spring";
import styled from "styled-components";
import Footer from "../../files/Footer/Footer";
import Header from "../../files/Header";
import { Helmet } from "react-helmet";

const PrivacyPolicy = () => {
  const [mounted, setMounted] = useState(false);
  const [userLocation, setUserLocation] = useState({
    coordinates: { latitude: 0, longitude: 0 },
  });
  const [showModal, setShowModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(
    localStorage.getItem("selectedCategory") || "products"
  );

    useEffect(() => {
        window.scrollTo(0, 0);
      }, []);
      
  useEffect(() => {
    localStorage.setItem("selectedCategory", selectedCategory);
  }, [selectedCategory]);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  const fadeIn = useSpring({
    opacity: mounted ? 1 : 0,
    from: { opacity: 0 },
    config: { duration: 800 },
  });

  return (
    <>
      <Helmet>
        <title>Privacy Policy | NearMe - Your Data Protection Commitment</title>
        <meta
          name="description"
          content="Read NearMe's comprehensive Privacy Policy. Learn how we collect, use, and protect your personal information while using our services."
        />
        <meta
          name="keywords"
          content="privacy policy, data protection, personal information, NearMe privacy, user data, GDPR, information security"
        />
        <meta property="og:title" content="Privacy Policy | NearMe" />
        <meta
          property="og:description"
          content="Your privacy matters. Learn how NearMe protects your personal information and data."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta name="robots" content="index, follow" />
        
      </Helmet>
      <Header
        hasShowOptions={true}
        userLocation={userLocation}
        setShowModal={setShowModal}
        setUserLocation={setUserLocation}
        showModal={showModal}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
      />
      <PrivacyPolicyContainer>
        <HeroSection>
          <HeroContent>
            <HeroTitle>Privacy Policy</HeroTitle>
            <HeroSubtitle>
              Your privacy is important to us. This policy explains how we
              collect, use, and protect your information.
            </HeroSubtitle>
          </HeroContent>
        </HeroSection>

        <ContentWrapper>
          <animated.div style={fadeIn}>
            <PolicyContent>
              <LastUpdated>Last Updated: June 15, 2023</LastUpdated>

              <MainTitle>Our Commitment to Your Privacy</MainTitle>
              <Paragraph>
                At NearMe, we are committed to protecting your privacy and
                ensuring the security of your personal information. This Privacy
                Policy outlines our practices regarding data collection, use,
                and disclosure when you use our services.
              </Paragraph>

              <SectionTitle>1. Information We Collect</SectionTitle>
              <Paragraph>
                We collect information to provide better services to all our
                users. The types of information we collect include:
              </Paragraph>

              <InfoCard>
                <CardTitle>Personal Information</CardTitle>
                <BulletList>
                  <li>Full name and contact details</li>
                  <li>Email address and phone number</li>
                  <li>Billing and shipping addresses</li>
                  <li>Account credentials</li>
                </BulletList>
              </InfoCard>

              <InfoCard>
                <CardTitle>Usage Data</CardTitle>
                <BulletList>
                  <li>IP address and device information</li>
                  <li>Browser type and version</li>
                  <li>Pages visited and time spent</li>
                  <li>Search queries and preferences</li>
                </BulletList>
              </InfoCard>

              <SectionTitle>2. How We Use Your Information</SectionTitle>
              <Paragraph>
                We use the information we collect for various purposes,
                including:
              </Paragraph>
              <NumberedList>
                <li>
                  <strong>Service Delivery:</strong> To provide and maintain our
                  services, authenticate users, and process transactions.
                </li>
                <li>
                  <strong>Improvements:</strong> To understand how our services
                  are used and to develop new features and functionality.
                </li>
                <li>
                  <strong>Communication:</strong> To send important notices,
                  updates, and marketing communications (where permitted).
                </li>
                <li>
                  <strong>Security:</strong> To detect, prevent, and address
                  technical issues, fraud, or other malicious activity.
                </li>
              </NumberedList>

              <SectionTitle>3. Data Sharing and Disclosure</SectionTitle>
              <Paragraph>
                We may share your information in the following circumstances:
              </Paragraph>
              <DefinitionList>
                <DefinitionItem>
                  <strong>Service Providers:</strong> With trusted third parties
                  who assist us in operating our services, subject to strict
                  confidentiality agreements.
                </DefinitionItem>
                <DefinitionItem>
                  <strong>Legal Compliance:</strong> When required by law or to
                  respond to valid legal processes.
                </DefinitionItem>
                <DefinitionItem>
                  <strong>Business Transfers:</strong> In connection with any
                  merger, sale of company assets, or acquisition.
                </DefinitionItem>
                <DefinitionItem>
                  <strong>Consent:</strong> With your explicit consent for
                  specific purposes.
                </DefinitionItem>
              </DefinitionList>

              <SectionTitle>4. Your Rights and Choices</SectionTitle>
              <Paragraph>
                You have certain rights regarding your personal information:
              </Paragraph>
              <BulletList>
                <li>
                  <strong>Access:</strong> Request a copy of your personal data
                </li>
                <li>
                  <strong>Correction:</strong> Update or correct inaccurate
                  information
                </li>
                <li>
                  <strong>Deletion:</strong> Request deletion of your personal
                  data
                </li>
                <li>
                  <strong>Opt-out:</strong> Object to or restrict certain
                  processing activities
                </li>
                <li>
                  <strong>Portability:</strong> Request transfer of your data to
                  another service
                </li>
              </BulletList>

              <SectionTitle>5. Data Security</SectionTitle>
              <Paragraph>
                We implement appropriate technical and organizational measures
                to protect your personal information, including:
              </Paragraph>
              <SecurityFeatures>
                <SecurityFeature>
                  <SecurityIcon>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                        stroke="#4F46E5"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M8 12L11 15L16 9"
                        stroke="#4F46E5"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </SecurityIcon>
                  <SecurityText>End-to-end encryption</SecurityText>
                </SecurityFeature>
                <SecurityFeature>
                  <SecurityIcon>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M19 21H5C3.89543 21 3 20.1046 3 19V5C3 3.89543 3.89543 3 5 3H19C20.1046 3 21 3.89543 21 5V19C21 20.1046 20.1046 21 19 21Z"
                        stroke="#4F46E5"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M12 8V13"
                        stroke="#4F46E5"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M12 16H12.01"
                        stroke="#4F46E5"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </SecurityIcon>
                  <SecurityText>Regular security audits</SecurityText>
                </SecurityFeature>
                <SecurityFeature>
                  <SecurityIcon>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z"
                        stroke="#4F46E5"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M19.4 15C19.2669 15.3016 19.227 15.6363 19.2853 15.9606C19.3436 16.2849 19.4975 16.5816 19.726 16.8101C19.9545 17.0386 20.2465 17.1874 20.5676 17.2358C20.8887 17.2841 21.2214 17.2294 21.515 17.0799C22.3466 16.6559 23.0814 16.0783 23.6733 15.3837C24.2652 14.6891 24.7014 13.8929 24.9539 13.044C25.2064 12.1951 25.2695 11.3123 25.1396 10.4485C25.0097 9.58466 24.6897 8.75973 24.2026 8.03151C23.7155 7.30329 23.0736 6.68948 22.323 6.23389"
                        stroke="#4F46E5"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M1.8 11.4C1.93314 11.0984 1.973 10.7637 1.9147 10.4394C1.8564 10.1151 1.7025 9.81836 1.474 9.58986C1.2455 9.36136 0.9535 9.21263 0.632365 9.16427C0.31123 9.11591 -0.0214145 9.17063 -0.315 9.32012C-1.14664 9.7441 -1.88139 10.3217 -2.47329 11.0163C-3.06519 11.7109 -3.50143 12.5071 -3.75393 13.356C-4.00643 14.2049 -4.06953 15.0877 -3.93963 15.9515C-3.80973 16.8153 -3.48973 17.6403 -3.00263 18.3685C-2.51553 19.0967 -1.87364 19.7105 -1.123 20.1661"
                        stroke="#4F46E5"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </SecurityIcon>
                  <SecurityText>Two-factor authentication</SecurityText>
                </SecurityFeature>
              </SecurityFeatures>

              <SectionTitle>6. International Data Transfers</SectionTitle>
              <Paragraph>
                Your information may be transferred to and maintained on
                computers located outside of your state, province, country, or
                other governmental jurisdiction where the data protection laws
                may differ from those of your jurisdiction.
              </Paragraph>

              <SectionTitle>7. Changes to This Policy</SectionTitle>
              <Paragraph>
                We may update our Privacy Policy from time to time. We will
                notify you of any changes by posting the new Privacy Policy on
                this page and updating the "Last Updated" date.
              </Paragraph>

              <SectionTitle>Contact Our Privacy Team</SectionTitle>
              <Paragraph>
                If you have any questions about this Privacy Policy or our
                privacy practices, please contact us:
              </Paragraph>
              <ContactCards>
                <ContactCard>
                  <ContactIcon>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z"
                        stroke="#4F46E5"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M22 6L12 13L2 6"
                        stroke="#4F46E5"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </ContactIcon>
                  <ContactDetails>
                    <ContactMethod>Email</ContactMethod>
                    <ContactValue>info@nearmeind.com</ContactValue>
                  </ContactDetails>
                </ContactCard>

                <ContactCard>
                  <ContactIcon>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M22 16.92V19.92C22 20.47 21.55 20.92 21 20.92H19.92C16.03 20.92 12.36 19.43 9.61001 16.68C6.86001 13.93 5.37001 10.26 5.37001 6.37001V5.27001C5.37001 4.72001 5.82001 4.27001 6.37001 4.27001H9.37001C9.92001 4.27001 10.37 4.72001 10.37 5.27001V10.27C10.37 10.82 9.92001 11.27 9.37001 11.27H7.62001"
                        stroke="#4F46E5"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M14.63 11.27H12.88C12.33 11.27 11.88 10.82 11.88 10.27V5.27001C11.88 4.72001 12.33 4.27001 12.88 4.27001H15.88C16.43 4.27001 16.88 4.72001 16.88 5.27001V10.27C16.88 10.82 16.43 11.27 15.88 11.27H14.13"
                        stroke="#4F46E5"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </ContactIcon>
                  <ContactDetails>
                    <ContactMethod>Phone</ContactMethod>
                    <ContactValue>+91 8544104411</ContactValue>
                  </ContactDetails>
                </ContactCard>

                <ContactCard>
                  <ContactIcon>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M21 10C21 17 12 23 12 23C12 23 3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z"
                        stroke="#4F46E5"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z"
                        stroke="#4F46E5"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </ContactIcon>
                  <ContactDetails>
                    <ContactMethod>Address</ContactMethod>
                    <ContactValue>
                      B2/1805 18th Floor, Panchsheel Greens 2, Greater Noida,
                      201306
                    </ContactValue>
                  </ContactDetails>
                </ContactCard>
              </ContactCards>
            </PolicyContent>
          </animated.div>
        </ContentWrapper>
      </PrivacyPolicyContainer>
      <Footer />
    </>
  );
};

// Styled Components
const PrivacyPolicyContainer = styled.div`
  min-height: 100vh;
  background-color: #f9fafb;
`;

const HeroSection = styled.div`
  background: #ed7a07;
  color: white;
  padding: 4rem 1.5rem;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
      circle at 20% 50%,
      rgba(255, 255, 255, 0.1) 0%,
      transparent 50%
    );
  }
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 1rem;
  @media (max-width: 768px) {
    font-size: 2.25rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  line-height: 1.6;
  opacity: 0.9;
  max-width: 700px;
  margin: 0 auto;
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
  position: relative;
  z-index: 5;
  margin-top: -3rem;
  @media (min-width: 768px) {
    padding: 3rem 2rem;
  }
`;

const PolicyContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  background: white;
  border-radius: 1rem;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.05);
  padding: 2.5rem;
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const LastUpdated = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  margin-bottom: 2rem;
  text-align: right;
  font-style: italic;
`;

const MainTitle = styled.h2`
  font-size: 2rem;
  font-weight: 800;
  color: #111827;
  margin-bottom: 1.5rem;
  position: relative;
  padding-bottom: 0.5rem;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 60px;
    height: 4px;
    background: linear-gradient(90deg, #4f46e5, #7c3aed);
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
  margin-top: 3rem;
  margin-bottom: 1.25rem;
  position: relative;
  padding-left: 1.5rem;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0.5rem;
    width: 8px;
    height: 8px;
    background: #4f46e5;
    border-radius: 50%;
  }

  @media (max-width: 768px) {
    font-size: 1.25rem;
    margin-top: 2.5rem;
  }
`;

const SubSectionTitle = styled.h4`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const Paragraph = styled.p`
  font-size: 1.0625rem;
  line-height: 1.75;
  color: #4b5563;
  margin-bottom: 1.5rem;
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const InfoCard = styled.div`
  background-color: #f8f9fa;
  border-left: 4px solid #4f46e5;
  border-radius: 0.5rem;
  padding: 1.5rem;
  margin: 1.5rem 0;
`;

const CardTitle = styled.h5`
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 1rem;
`;

const DefinitionList = styled.ul`
  list-style-type: none;
  padding-left: 0;
  margin: 1.5rem 0;
`;

const DefinitionItem = styled.li`
  font-size: 1.0625rem;
  line-height: 1.75;
  color: #4b5563;
  margin-bottom: 0.75rem;
  padding-left: 1.5rem;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0.75rem;
    width: 6px;
    height: 6px;
    background-color: #4f46e5;
    border-radius: 50%;
  }

  strong {
    color: #111827;
  }
`;

const BulletList = styled.ul`
  list-style-type: none;
  padding-left: 1.5rem;
  margin: 1.5rem 0;

  li {
    font-size: 1.0625rem;
    line-height: 1.75;
    color: #4b5563;
    margin-bottom: 0.5rem;
    position: relative;
    padding-left: 1.25rem;

    &::before {
      content: "•";
      color: #4f46e5;
      font-weight: bold;
      position: absolute;
      left: 0;
    }

    strong {
      color: #111827;
    }
  }
`;

const NumberedList = styled.ol`
  list-style-type: none;
  padding-left: 1.5rem;
  margin: 1.5rem 0;
  counter-reset: item;

  li {
    font-size: 1.0625rem;
    line-height: 1.75;
    color: #4b5563;
    margin-bottom: 1rem;
    position: relative;
    padding-left: 2.5rem;

    &::before {
      counter-increment: item;
      content: counter(item);
      position: absolute;
      left: 0;
      top: 0;
      width: 1.75rem;
      height: 1.75rem;
      background: linear-gradient(135deg, #4f46e5, #7c3aed);
      color: white;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 0.875rem;
      font-weight: 600;
    }

    strong {
      color: #111827;
    }
  }
`;

const SecurityFeatures = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin: 2rem 0;
`;

const SecurityFeature = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background-color: #f5f3ff;
  border-radius: 0.5rem;
`;

const SecurityIcon = styled.div`
  width: 48px;
  height: 48px;
  background-color: #eef2ff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const SecurityText = styled.span`
  font-size: 1rem;
  font-weight: 500;
  color: #111827;
`;

const ContactCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin: 2rem 0;
`;

const ContactCard = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1.5rem;
  background-color: #f8f9fa;
  border-radius: 0.75rem;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  }
`;

const ContactIcon = styled.div`
  width: 48px;
  height: 48px;
  background-color: #eef2ff;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const ContactDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const ContactMethod = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: #6b7280;
  margin-bottom: 0.25rem;
`;

const ContactValue = styled.span`
  font-size: 1rem;
  font-weight: 500;
  color: #111827;
  line-height: 1.5;
`;

export default PrivacyPolicy;
